import { SETTINGS, anchor } from './setting-anchors.types';

import type { SettingAnchor } from './setting-anchors.types';

/**
 * The `/settings/workspace` anchors — sixteen cards across four tabs.
 *
 * Split out of the registry at the ~300-line ceiling, the same shape
 * `sidebar-definitions.workspace.ts` takes beside its own list, and spread back
 * into the one map next door so there is still exactly one `SETTING_ANCHORS`.
 *
 * The registry's own header used to warn that "a registry split across two files
 * is a registry with two places to forget an entry", and that warning was right
 * when nothing checked. It is weaker now:
 * `settings-search-coverage.convention.test.ts` fails on an anchor no card
 * renders, on a key that does not resolve in six catalogs, and on a
 * settings-like route that is neither indexed nor named in its ratchet. A
 * forgotten entry is a red test rather than a silent gap.
 */
export const WORKSPACE_SETTING_ANCHORS = {
  // `/settings/workspace` — General
  'workspace-identity': anchor(
    'setting-workspace-identity',
    SETTINGS,
    'workspacesettings.identityHeading',
    'settings',
    'view',
    { keywordsKey: 'settingSearch.workspaceIdentity', pane: 'general' },
  ),
  'workspace-access': anchor(
    'setting-workspace-access',
    SETTINGS,
    'workspacesettings.accessHeading',
    'settings',
    'view',
    { keywordsKey: 'settingSearch.workspaceAccess', pane: 'general' },
  ),
  'workspace-name': anchor(
    'setting-workspace-name',
    SETTINGS,
    'workspacesettings.rename.heading',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.workspaceName', pane: 'general', requiresNonPersonal: true },
  ),
  'conversation-model-preference': anchor(
    'setting-conversation-model-preference',
    SETTINGS,
    'workspacesettings.conversationModelPreference.heading',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.conversationModelPreference', pane: 'general' },
  ),
  // D13 / WF-3.8 — the posture that SETS the two dials below it. Listed first
  // for the same reason it renders first: an operator searching settings for
  // "what can the agent do" should meet the one control before its components.
  'autonomy-mode': anchor(
    'setting-autonomy-mode',
    SETTINGS,
    'autonomy.title',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.autonomyMode', pane: 'general' },
  ),
  'conversation-execution-bias': anchor(
    'setting-conversation-execution-bias',
    SETTINGS,
    'workspacesettings.conversationExecutionBias.heading',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.conversationExecutionBias', pane: 'general' },
  ),
  // ADR-0444 — the workspace two-person-rule switch for destructive enablement.
  'dual-control': anchor(
    'setting-dual-control',
    SETTINGS,
    'workspacesettings.dualControl.heading',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.dualControl', pane: 'general' },
  ),

  // `/settings/workspace` — Data & privacy
  'conversation-persistence': anchor(
    'setting-conversation-persistence',
    SETTINGS,
    'workspacesettings.conversationPersistence.heading',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.conversationPersistence', pane: 'data' },
  ),
  'conversation-retention': anchor(
    'setting-conversation-retention',
    SETTINGS,
    'workspacesettings.conversationRetention.heading',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.conversationRetention', pane: 'data' },
  ),
  // `/settings/workspace` — Backups (ADR-0766). Owner/admin, like the tab: the
  // gate reads the same `settings:admin` the server enforces, so a viewer's
  // search never offers a jump to a tab the page withholds.
  'config-checkpoints': anchor(
    'setting-config-checkpoints',
    SETTINGS,
    'workspacesettings.backups.status.heading',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.configCheckpoints', pane: 'backups' },
  ),
  'config-checkpoint-as-of': anchor(
    'setting-config-checkpoint-as-of',
    SETTINGS,
    'workspacesettings.backups.asOf.heading',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.configCheckpointAsOf', pane: 'backups' },
  ),
  'config-checkpoint-history': anchor(
    'setting-config-checkpoint-history',
    SETTINGS,
    'workspacesettings.backups.list.heading',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.configCheckpointHistory', pane: 'backups' },
  ),
  'conversation-forwarding': anchor(
    'setting-conversation-forwarding',
    SETTINGS,
    'workspacesettings.conversationForwarding.heading',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.conversationForwarding', pane: 'data' },
  ),
  'sentry-forwarding': anchor(
    'setting-sentry-forwarding',
    SETTINGS,
    'workspacesettings.sentryForwarding.heading',
    'settings',
    'admin',
    { keywordsKey: 'settingSearch.sentryForwarding', pane: 'data' },
  ),

  // `/settings/workspace` — Danger zone
  'leave-workspace': anchor(
    'setting-leave-workspace',
    SETTINGS,
    'workspacesettings.leave.heading',
    'settings',
    'view',
    { keywordsKey: 'settingSearch.leaveWorkspace', pane: 'danger', requiresNonPersonal: true },
  ),
  'transfer-ownership': anchor(
    'setting-transfer-ownership',
    SETTINGS,
    'workspacesettings.transfer.heading',
    'settings',
    'admin',
    {
      keywordsKey: 'settingSearch.transferOwnership',
      pane: 'danger',
      requiresOwner: true,
      requiresNonPersonal: true,
    },
  ),
  'delete-workspace': anchor(
    'setting-delete-workspace',
    SETTINGS,
    'workspacesettings.delete.heading',
    'settings',
    'admin',
    {
      keywordsKey: 'settingSearch.deleteWorkspace',
      pane: 'danger',
      requiresOwner: true,
      requiresNonPersonal: true,
    },
  ),
} as const satisfies Record<string, SettingAnchor>;
